// apps/client/src/hooks/useTrailRunOffPercentage.ts
// Lightweight hook that fetches the trail run-off percentage for the dashboard widget
// and the Portfolio Run-Off Rate stat card. Reuses the existing getTrailRunOffReport
// Cloud Function.

import { useState, useEffect, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export type RunOffHealth = 'healthy' | 'warning' | 'critical';

/**
 * Opening versus closing trail book across the window, as returned by
 * getTrailRunOffReport.bookChange. Mirrors the server type; null when the
 * window holds fewer than two months of trail data.
 */
export interface TrailBookChange {
  openingMonth: string;
  closingMonth: string;
  openingLoans: number;
  closingLoans: number;
  openingBalanceCents: number;
  closingBalanceCents: number;
  loanCountChangePct: number | null;
  balanceChangePct: number | null;
}

export interface UseTrailRunOffPercentageReturn {
  runOffPercentage: number | null;
  health: RunOffHealth;
  /**
   * Plain opening-vs-closing book change over the same window. Deliberately a
   * different measure from runOffPercentage (which only counts a loan once it
   * has gone 6+ months without trail). null until loaded or when unavailable.
   */
  bookChange: TrailBookChange | null;
  /** Human-readable period label, e.g. "Feb 2025 – Jan 2026" */
  periodLabel: string;
  loading: boolean;
  error: string | null;
}

export interface UseTrailRunOffPercentageParams {
  companyId: string;
  officeId?: string;
  brokerId?: string;
  /**
   * Loan-book scope, resolved by resolveLoanBookSelection(). Pages with a
   * loan-book filter MUST pass these so the card agrees with the sibling cards
   * (before 2026-10 the card blended every book regardless of the filter).
   */
  templateId?: string;
  entityCode?: string;
}

/**
 * Fetches trail run-off percentage using a rolling 12-month window ending last month.
 *
 * Example: In February 2026, the window is Feb 2025 – Jan 2026.
 * This gives a full 12 months of data (matching Aaron Christie-David's annual
 * benchmark) while staying current (max 1 month old).
 *
 * Thresholds (from industry benchmarks, Aaron Christie-David):
 *   < 8%  = healthy (green)
 *   8-12% = warning (amber)
 *   > 12% = critical (red)
 */
export function useTrailRunOffPercentage({
  companyId,
  officeId,
  brokerId,
  templateId,
  entityCode,
}: UseTrailRunOffPercentageParams): UseTrailRunOffPercentageReturn {
  const [runOffPercentage, setRunOffPercentage] = useState<number | null>(null);
  const [bookChange, setBookChange] = useState<TrailBookChange | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Compute rolling 12-month window: 12 months back from start of current month
  const { startMonth, endMonth, periodLabel } = useMemo(() => {
    const now = new Date();
    // End = last day of previous month
    const endDate = new Date(now.getFullYear(), now.getMonth(), 0); // day 0 = last day of prev month
    // Start = first day, 11 months before endDate's month (12 months total)
    const startDate = new Date(endDate.getFullYear(), endDate.getMonth() - 11, 1);

    const pad = (n: number) => String(n).padStart(2, '0');
    const sm = `${startDate.getFullYear()}-${pad(startDate.getMonth() + 1)}-01`;
    const em = `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-${pad(endDate.getDate())}`;

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const label = `${monthNames[startDate.getMonth()]} ${startDate.getFullYear()} – ${monthNames[endDate.getMonth()]} ${endDate.getFullYear()}`;

    return { startMonth: sm, endMonth: em, periodLabel: label };
  }, []);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    const fetchRunOff = async () => {
      setLoading(true);
      setError(null);

      try {
        const getTrailRunOffReport = httpsCallable(functions, 'getTrailRunOffReport');
        const result = await getTrailRunOffReport({
          companyId,
          officeId,
          brokerId,
          templateId,
          entityCode,
          year: new Date().getFullYear() - 1, // fallback, overridden by startMonth/endMonth
          yearType: 'calendar',
          startMonth,
          endMonth,
        });

        const data = result.data as { runOffPercentage: number; bookChange?: TrailBookChange | null };
        setRunOffPercentage(data.runOffPercentage ?? null);
        setBookChange(data.bookChange ?? null);
      } catch (err: any) {
        console.error('[useTrailRunOffPercentage] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch run-off data');
        setRunOffPercentage(null);
        setBookChange(null);
      } finally {
        setLoading(false);
      }
    };

    fetchRunOff();
  }, [companyId, officeId, brokerId, templateId, entityCode, startMonth, endMonth]);

  const health: RunOffHealth =
    runOffPercentage === null
      ? 'healthy'
      : runOffPercentage > 12
        ? 'critical'
        : runOffPercentage >= 8
          ? 'warning'
          : 'healthy';

  return { runOffPercentage, health, bookChange, periodLabel, loading, error };
}
